import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import TokensPercentagesFullList from '../../Common/TokensPercentagesFullList/TokensPercentagesFullList';
import TokensPercentagesBar from '../../Common/TokensPercentagesBar/TokensPercentagesBar';
import './EditTokensModal.scss';
import { compareStrings, getValueByMultiProps } from '../../../utils/mapper';
import ChevronRightIcon from '../../Common/Icons/ChevronRightIcon';
import TradeOrdersConfirm from './TradeOrdersConfirm';
import { useAssetsSignatures } from '../../../hooks/useAssetsSignatures';
import configJson from '../../../config/config.json';
import { closeModal } from '../../../redux/actions/modalActions';
import { displayFloats } from '../../../utils/numbersDisplay';
import { getBaseToken } from '../../../services/createHPoolService';

const UpdatedHPoolList = ({ modifiedModalTokens, baseTokens, closeModal, assets }) => {
  const [, , , , , unSignedTx] = useAssetsSignatures('TradeOrder');
  const tokenId = (token) => getValueByMultiProps(token, ['token_id']);
  const baseCoinName = getBaseToken(baseTokens)?.token_symbol?.toLowerCase();

  const baseCoin = useMemo(
    () => modifiedModalTokens?.find((order) => compareStrings(baseCoinName, order?.token_symbol)),
    [baseCoinName],
  );

  useEffect(() => {
    if (!unSignedTx.length) closeModal();
  }, [unSignedTx.length]);

  const getTokensPrevAndCurrent = (token) => {
    const id = tokenId(token);
    const prevToken = baseTokens.find((token) => tokenId(token) === id) || {};
    const newToken = modifiedModalTokens.find((token) => tokenId(token) === id) || {};
    let { percentage: prevValue = 0 } = prevToken;
    let { percentage: newValue = 0 } = newToken;

    prevValue = displayFloats(prevValue);
    newValue = displayFloats(newValue);

    return [prevValue, newValue, prevToken, newToken];
  };

  const getTokenOrder = useCallback((tradeOrder) => {
    const [_prevValue, _newValue] = getTokensPrevAndCurrent(tradeOrder);
    const diff = _newValue - _prevValue;
    const orderType = diff > 0 ? 'Buy' : diff < 0 || (!_newValue && diff) ? 'Sell' : 'HIDDEN';

    // at Buy trade order eth will alawys be the base token
    const baseToken = orderType === 'Buy' ? baseCoin : tradeOrder;
    const forToken = orderType === 'Sell' ? baseCoin : tradeOrder;
    const absoluteDiff = Math.abs(diff);
    return { ...tradeOrder, orderType, percentage: _newValue, baseToken, forToken, diff: absoluteDiff };
  }, []);

  const filteredFromEth = useMemo(
    () => (baseCoin ? modifiedModalTokens?.filter((order) => order?.id !== baseCoin.id) : modifiedModalTokens),
    [modifiedModalTokens, baseCoin],
  );

  const tokensToSign = useMemo(
    () => filteredFromEth.map(getTokenOrder).filter((token) => token.orderType !== 'HIDDEN'),
    [filteredFromEth.length],
  );

  const renderPercentage = useCallback(
    (token) => {
      const [prevValue = 0, newValue] = getTokensPrevAndCurrent(token);
      const hasNoChanges = !newValue || prevValue === newValue;
      const renderedValue = newValue || prevValue || 0;
      return (
        <>
          {hasNoChanges ? (
            <span>{`${renderedValue}%`}</span>
          ) : (
            <div className="flex-centered gap-1">
              <span>{`${prevValue}%`}</span>
              <span className="rotate-270">
                <ChevronRightIcon />
              </span>
              <span>{`${newValue}%`}</span>
            </div>
          )}
        </>
      );
    },
    [modifiedModalTokens, baseTokens],
  );

  // const isAllInassetSignature = useMemo(
  //   () => tokensToSign.map((token) => token.token_id).every((id) => isInAssetSignature(id)),
  //   [tokensToSign.length, assetSignatures],
  // );

  return (
    <div>
      <TradeOrdersConfirm orders={tokensToSign} getTokenChanges={getTokenOrder} />
      <div className="updated-hpool-list-container">
        <div className="tokens-percentagesbar-container">
          <TokensPercentagesBar tokens={assets} />
        </div>
        <TokensPercentagesFullList renderPercentage={renderPercentage} tokens={assets} />
        {/* {!isAllInassetSignature && <Button text="hpool.sign_all" className="button green" onClick={signAllTx} />} */}
      </div>
    </div>
  );
};

const mapStateToProps = ({ createHPool }) => ({
  addedTokens: createHPool.addedTokens,
  tradeOrders: createHPool.tradeOrders,
  assets: createHPool.hpool?.assets || [],
});

const mapDispatchToProps = {
  closeModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdatedHPoolList);

UpdatedHPoolList.propTypes = {
  modifiedModalTokens: PropTypes.array.isRequired,
  baseTokens: PropTypes.array.isRequired,
  closeModal: PropTypes.func.isRequired,
  assets: PropTypes.array.isRequired,
};
